import { FC } from 'react';

import {
  Button,
  Col,
  Divider,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import IconPlus from '@/assets/icons/icon-plus.svg?react';
import { StorageSystemForm } from '@/types/storageSystem';
import { normalizePositiveNumericValuesInput } from '@/utils';

type Props = {
  disabled?: boolean;
  form: FormInstance<StorageSystemForm>;
};

const RateCapability: FC<Props> = ({ disabled = false, form }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  function appendToArrayField(fieldName: keyof StorageSystemForm) {
    const curValue = form.getFieldValue(fieldName) || [];
    form.setFieldValue(fieldName, [...curValue, {}]);
  }
  return (
    <Form.Item
      wrapperCol={{ span: 24 }}
      style={{
        border: '0.5px solid',
        borderRadius: '10px',
        borderColor: theme.colors.grayLightAccent,
      }}
      className="p-16"
    >
      <Typography.Text type="secondary">
        {t('storageSystem.form.rateCapability.description')}
        <br />
        <br />
        {t('storageSystem.form.rateCapability.description2')}
        <br />
        <br />
        {t('storageSystem.form.rateCapability.highPowerApplications')}
        <br />
        {t('storageSystem.form.rateCapability.highEnergyApplications')}
      </Typography.Text>
      <Row className="mt-24" gutter={20}>
        <Col span={12}>
          <Form.Item
            label={t('storageSystem.form.rateCapability.minAllowedCRate')}
            name="minAllowedCRateForCharging"
            tooltip={t(
              'storageSystem.form.rateCapability.minAllowedCRateTooltip'
            )}
            dependencies={['maxAllowedCRateForCharging']}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.rateCapability.minAllowedCRateRequired'
                ),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const maxC = parseFloat(
                    getFieldValue('maxAllowedCRateForCharging')
                  );
                  const minC = parseFloat(value);
                  if (minC === 0) {
                    return Promise.reject(
                      t(
                        'storageSystem.form.rateCapability.minAllowedCRateCannotBeZero'
                      )
                    );
                  }
                  if (maxC !== undefined && minC > maxC) {
                    return Promise.reject(
                      t(
                        'storageSystem.form.rateCapability.minMustBeLessThanMax'
                      )
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            normalize={normalizePositiveNumericValuesInput}
          >
            <Input
              placeholder={t(
                'storageSystem.form.rateCapability.minAllowedCRate'
              )}
              addonAfter="C"
              className="w-100"
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t('storageSystem.form.rateCapability.maxAllowedCRate')}
            name="maxAllowedCRateForCharging"
            tooltip={t(
              'storageSystem.form.rateCapability.maxAllowedCRateTooltip'
            )}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.rateCapability.maxAllowedCRateRequired'
                ),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const minC = parseFloat(
                    getFieldValue('minAllowedCRateForCharging')
                  );
                  const maxC = parseFloat(value);
                  if (maxC === 0) {
                    return Promise.reject(
                      t(
                        'storageSystem.form.rateCapability.maxAllowedCRateCannotBeZero'
                      )
                    );
                  }
                  if (minC !== undefined && maxC < minC) {
                    return Promise.reject(
                      t(
                        'storageSystem.form.rateCapability.maxMustBeGreaterThanMin'
                      )
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            normalize={normalizePositiveNumericValuesInput}
          >
            <Input
              placeholder={t(
                'storageSystem.form.rateCapability.maxAllowedCRate'
              )}
              addonAfter="C"
              step={0.01}
              className="w-100"
              disabled={disabled}
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider
        style={{
          border: '0.5px solid',
          borderColor: theme.colors.grayLightAccent,
        }}
        className="mt-24 mb-24"
      />
      <Form.List initialValue={[{}]} name="rateCapabilities">
        {(fields, { remove }) => (
          <Col>
            {fields.map(({ key, name, ...restField }) => (
              <Row gutter={10} key={key} align="bottom" wrap={false}>
                <Col span={5.5}>
                  <Form.Item
                    label={t('storageSystem.form.rateCapability.type')}
                    {...restField}
                    name={[name, 'mode']}
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder={t(
                        'storageSystem.form.rateCapability.selectOption'
                      )}
                      style={{ minWidth: '200px', height: '34px' }}
                      disabled={disabled}
                    >
                      <Select.Option value={0}>
                        {t('storageSystem.form.rateCapability.charge')}
                      </Select.Option>
                      <Select.Option value={1}>
                        {t('storageSystem.form.rateCapability.discharge')}
                      </Select.Option>
                      <Select.Option value={2}>
                        {t(
                          'storageSystem.form.rateCapability.chargeAndDischarge'
                        )}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={5.5}>
                  <Form.Item
                    label={t('storageSystem.form.rateCapability.cRate')}
                    {...restField}
                    name={[name, 'cRate']}
                    rules={[{ required: true }]}
                    normalize={normalizePositiveNumericValuesInput}
                  >
                    <Input
                      placeholder={t('storageSystem.form.rateCapability.cRate')}
                      addonAfter="C"
                      className="w-100"
                      disabled={disabled}
                    />
                  </Form.Item>
                </Col>
                <Col span={5.5}>
                  <Form.Item
                    {...restField}
                    label={t('storageSystem.form.rateCapability.cycleTime')}
                    name={[name, 'cycleLife']}
                    rules={[{ required: true }]}
                    normalize={normalizePositiveNumericValuesInput}
                  >
                    <Input
                      placeholder={t(
                        'storageSystem.form.rateCapability.cycleTime'
                      )}
                      addonAfter={t('storageSystem.form.rateCapability.cycle')}
                      className="w-100"
                      disabled={disabled}
                    />
                  </Form.Item>
                </Col>
                <Col span={5.5}>
                  <Form.Item
                    {...restField}
                    label={t(
                      'storageSystem.form.rateCapability.roundTripEfficiency'
                    )}
                    name={[name, 'batteryRoundTripEfficiency']}
                    rules={[{ required: true }]}
                    normalize={normalizePositiveNumericValuesInput}
                  >
                    <Input
                      placeholder={t(
                        'storageSystem.form.rateCapability.roundTripEfficiency'
                      )}
                      className="w-100"
                      addonAfter="%"
                      disabled={disabled}
                    />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Form.Item>
                    <Button
                      disabled={disabled || fields.length === 1}
                      danger
                      block
                      onClick={() => {
                        remove(name);
                      }}
                      style={{ height: '34px' }}
                    >
                      {t('storageSystem.form.rateCapability.delete')}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            ))}
            <Row className="mt-12" align="middle" justify="end">
              <Button
                icon={<IconPlus fill={theme.colors.bluePrimary} />}
                type="dashed"
                onClick={() => appendToArrayField('rateCapabilities')}
                style={{ height: '34px' }}
                disabled={disabled}
                data-testid="add-rate-capability-btn"
              >
                {t('storageSystem.form.rateCapability.addCRate')}
              </Button>
            </Row>
          </Col>
        )}
      </Form.List>
      <Divider
        style={{
          border: '0.5px solid',
          borderColor: theme.colors.grayLightAccent,
        }}
        className="mt-24 mb-24"
      />
    </Form.Item>
  );
};

export default RateCapability;
